import { ProductType } from 'types/chart'
import styles from './PriceHistoryElement.module.scss'
import PriceHistoryChart from './PriceHistoryChart'
import { useState, useRef, useEffect, useMemo } from 'react'
import { MinMaxData } from '../types'
import { PriceHistoryElementProps, PriceHistoryMetrics } from './types'
import { Button, Icon, LoadingSkeleton, Select, Toggle } from '@boltenergy-be/design-system'
import { useTranslation } from 'next-i18next/pages'

const PriceHistoryElement = ({ priceHistoryData, currentDateString, minStartDateString }: PriceHistoryElementProps) => {
  // Local constants
  //convert strings back to date
  const minStartDate = new Date(minStartDateString)
  const currentDate = new Date(currentDateString)

  // i18n
  const { t } = useTranslation('common')

  // Local state
  const [activePriceHistory, setActivePriceHistory] = useState<PriceHistoryMetrics | undefined>(priceHistoryData)
  const [activeProduct, setActiveProduct] = useState<ProductType>(ProductType.ELECTRICITY)
  const [priceHistoryMonths, setPriceHistoryMonths] = useState<string[] | undefined>([])
  const [minMaxData, setMinMaxData] = useState<MinMaxData | null>(null)
  const [period, setPeriod] = useState(currentDate.getFullYear())

  // Refs
  const prevPeriodRef = useRef<number>(null)

  // Memoized constants
  const years = useMemo<number[]>(() => {
    const years = []
    for (let year = minStartDate.getFullYear(); year <= currentDate.getFullYear(); year++) {
      years.push(year)
    }
    return years
  }, [])

  /**
   * Get the active price history data and sets the corresponding states
   */
  const fetchAndSetPriceHistoryData = async () => {
    const activePriceHistoryData: PriceHistoryMetrics = { electricity: [], gas: [] }
    for (const key of Object.keys(priceHistoryData)) {
      activePriceHistoryData[key] = priceHistoryData[key].filter(
        (dataPoint) =>
          //electricity needs special handling for the last day of the year
          (dataPoint.date.includes(period.toString()) || dataPoint.date.includes(`${period - 1}-12-31`)) &&
          !dataPoint.date.includes(`${period}-12-31`)
      )
    }
    setActivePriceHistory(activePriceHistoryData)
  }

  // useEffect hooks
  useEffect(() => {
    if (priceHistoryData) {
      prevPeriodRef.current = period
      fetchAndSetPriceHistoryData()
    }
  }, [priceHistoryData, period])

  // Get x-axis data for PriceHistoryChart
  useEffect(() => {
    if (activePriceHistory) {
      setPriceHistoryMonths(activePriceHistory?.[activeProduct]?.map((dataPoint) => dataPoint.date))
    }
  }, [activeProduct, activePriceHistory, period])

  // Get min and max data for PriceHistoryChart
  useEffect(() => {
    const minMax = activePriceHistory?.[activeProduct].reduce(
      (acc: MinMaxData, { price }) => {
        if (price < acc.minDataValue) {
          acc.minDataValue = price
        }
        if (price > acc.maxDataValue) {
          acc.maxDataValue = price
        }
        return acc
      },
      { minDataValue: Infinity, maxDataValue: -Infinity }
    )
    setMinMaxData(minMax)
  }, [activeProduct, activePriceHistory])

  /**
   * Switch active product type
   */
  const switchActiveType = () => {
    setActiveProduct((prev) => (prev === ProductType.ELECTRICITY ? ProductType.GAS : ProductType.ELECTRICITY))
  }

  /**
   * Switch active period by + or - 1 year
   * @param {'prev' | 'next'} change
   */
  const switchActivePeriod = (change: 'prev' | 'next') => {
    if (change === 'prev') {
      setPeriod((prev) => prev - 1)
    } else if (change === 'next') {
      setPeriod((prev) => prev + 1)
    }
  }

  /**
   * Select active period from dropdown
   * @param {number} period - selected period
   */
  const selectActivePeriod = (period: number) => {
    setPeriod(period)
  }

  return (
    <main className={styles.graph}>
      {/* NAVIGATION */}
      <div className={styles['control-elements']}>
        {/* PERIOD SELECTION */}
        <div className={styles['period-selection']}>
          <Button
            type="button"
            onClick={() => switchActivePeriod('prev')}
            className={styles['period-btn']}
            disabled={period <= minStartDate.getFullYear()}
            leadingIcon="chevronLeft"
            size="small"
            variant="tertiary"
          />

          <Select
            label="Period"
            hiddenLabel
            name="period"
            aria-label="period"
            value={period}
            className={styles['period-dropdown']}
            onChange={(e) => selectActivePeriod(Number(e.target.value))}
          >
            {years.map((year) => (
              <option value={year} key={year}>
                {year}
              </option>
            ))}
          </Select>

          <Button
            type="button"
            onClick={() => switchActivePeriod('next')}
            className={styles['period-btn']}
            disabled={period >= new Date().getFullYear()}
            leadingIcon="chevronRight"
            size="small"
            variant="tertiary"
          />
        </div>

        {/* PRODUCT SELECTION */}
        <Toggle
          active={activeProduct}
          onClick={switchActiveType}
          className={styles.toggle}
          options={[
            { value: ProductType.ELECTRICITY, label: <Icon name="electricity" size={20} aria-label={t('electricity')} /> },
            { value: ProductType.GAS, label: <Icon name="gas" size={20} aria-label={t('gas')} /> }
          ]}
        />
      </div>

      {/* CHART */}
      {activePriceHistory && activePriceHistory?.[activeProduct] && minMaxData ? (
        <PriceHistoryChart
          data={activePriceHistory[activeProduct]}
          activeProduct={activeProduct}
          months={priceHistoryMonths}
          minMaxData={minMaxData}
        />
      ) : (
        <LoadingSkeleton>
          <LoadingSkeleton.Rectangle aspectRatio="4 / 1" />
          <LoadingSkeleton.Rectangle height={30} />
        </LoadingSkeleton>
      )}
    </main>
  )
}

export default PriceHistoryElement
